import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  ButtonGroup,
  SkeletonBodyText,
  Banner,
  InlineGrid,
  Tooltip,
  Icon,
  Select,
} from '@shopify/polaris';
import { InfoIcon } from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useDashboard } from '../hooks';
import { getDailyMockups } from '../api';
import { STAGE_LABELS, STAGE_TONES, type WorkflowStage, type DailyMockupEntry } from '../types';

const ALL_STAGES: WorkflowStage[] = [
  'ORDER_RECEIVED',
  'DIGITAL_MOCKUP',
  'AWAITING_APPROVAL',
  'APPROVED',
  'DIGITIZED_MOLD_SENT',
  'DIGITIZED_MOLD_RECEIVED',
  'IN_PRODUCTION',
  'IN_QA',
  'READY_TO_SHIP',
  'SHIPPED',
  'COMPLETE',
  'ARCHIVED',
];

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatApprovalTime(hours: number | null): string {
  if (hours === null) return 'N/A';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours} hrs`;
  return `${Math.round(hours / 24)} days`;
}

// Metric card
function MetricCard({
  title,
  value,
  subtitle,
  onClick,
}: {
  title: string;
  value: string;
  subtitle?: string;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <Card>
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm" tone="subdued">
            {title}
          </Text>
          <Text as="p" variant="headingXl">
            {value}
          </Text>
          {subtitle && (
            <Text as="p" variant="bodySm" tone="subdued">
              {subtitle}
            </Text>
          )}
        </BlockStack>
      </Card>
    </div>
  );
}

// Loading skeleton
function DashboardSkeleton() {
  return (
    <Layout>
      <Layout.Section>
        <InlineGrid columns={3} gap="400">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <BlockStack gap="200">
                <SkeletonBodyText lines={1} />
                <SkeletonBodyText lines={1} />
              </BlockStack>
            </Card>
          ))}
        </InlineGrid>
      </Layout.Section>
      <Layout.Section>
        <InlineGrid columns={2} gap="400">
          <Card>
            <BlockStack gap="200">
              <SkeletonBodyText lines={1} />
              <SkeletonBodyText lines={1} />
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="400">
              <SkeletonBodyText lines={1} />
              <SkeletonBodyText lines={3} />
            </BlockStack>
          </Card>
        </InlineGrid>
      </Layout.Section>
    </Layout>
  );
}

type TimeRange = '7d' | '30d' | '90d' | 'all';

function filterByRange(entries: DailyMockupEntry[], range: TimeRange): DailyMockupEntry[] {
  if (range === 'all') return entries;
  const now = new Date();
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days)
    .toISOString()
    .split('T')[0];
  return entries.filter((e) => e.date >= cutoff);
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useDashboard();

  // Mockup chart state
  const [mockupData, setMockupData] = useState<DailyMockupEntry[]>([]);
  const [mockupCountries, setMockupCountries] = useState<string[]>([]);
  const [mockupLoading, setMockupLoading] = useState(true);
  const [mockupRange, setMockupRange] = useState<TimeRange>('30d');
  const [mockupCountry, setMockupCountry] = useState<string>('');

  const fetchMockupData = useCallback(async (country?: string) => {
    setMockupLoading(true);
    try {
      const report = await getDailyMockups(country || undefined);
      setMockupData(report.entries);
      // Only update countries list on initial load (unfiltered)
      if (!country) {
        setMockupCountries(report.countries || []);
      }
    } catch {
      // Non-critical — chart just won't show
    } finally {
      setMockupLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMockupData();
  }, [fetchMockupData]);

  // Refetch when country filter changes
  useEffect(() => {
    fetchMockupData(mockupCountry);
  }, [mockupCountry, fetchMockupData]);

  const filteredMockupData = useMemo(
    () => filterByRange(mockupData, mockupRange),
    [mockupData, mockupRange]
  );

  const hasJotformData = useMemo(
    () => mockupData.some((e) => e.jotform > 0),
    [mockupData]
  );

  return (
    <Page
      title="Six Hats Production Pipeline"
      primaryAction={{
        content: 'Settings',
        onAction: () => navigate('/settings'),
      }}
    >
      {error && (
        <Layout.Section>
          <Banner
            title="Error loading dashboard"
            tone="critical"
            action={{ content: 'Retry', onAction: refetch }}
          >
            <p>{error}</p>
          </Banner>
        </Layout.Section>
      )}

      {loading && !data ? (
        <DashboardSkeleton />
      ) : (
        <Layout>
          {/* Nav links */}
          <Layout.Section>
            <InlineStack gap="200" wrap>
              <ButtonGroup>
                <Button onClick={() => navigate('/orders')}>Customer Orders</Button>
                <Button onClick={() => navigate('/mockups')}>Mockups</Button>
                <Button onClick={() => navigate('/digitization')}>External Services</Button>
                <Button onClick={() => navigate('/approvals')}>Approvals</Button>
                <Button onClick={() => navigate('/production')}>Production & Shipping</Button>
              </ButtonGroup>
            </InlineStack>
          </Layout.Section>

          {/* Top row: 3 metric cards */}
          <Layout.Section>
            <InlineGrid columns={3} gap="400">
              <MetricCard
                title="Mockups Received"
                value={String(data?.mockupsReceivedCount ?? 0)}
                subtitle={`${data?.mockupsReceivedAllTime ?? 0} all-time`}
                onClick={() => navigate('/mockups')}
              />
              <MetricCard
                title="Median Approval Time"
                value={formatApprovalTime(data?.medianApprovalHours ?? null)}
                subtitle="mockup sent → approved"
              />
              <MetricCard
                title="Needs Invoice"
                value={String(data?.needsInvoiceCount ?? 0)}
                subtitle="approved drafts awaiting invoice"
                onClick={() => navigate('/approvals?tab=NEEDS_INVOICE')}
              />
            </InlineGrid>
          </Layout.Section>

          {/* Second row: Revenue cards */}
          <Layout.Section>
            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
              <Card>
                <BlockStack gap="200">
                  <InlineStack gap="100" blockAlign="center">
                    <Text as="h3" variant="headingSm" tone="subdued">
                      Paid Revenue
                    </Text>
                    <Tooltip content="Sum of Shopify order totals for paid orders (non-draft orders + completed draft orders). Orders without price data are excluded." dismissOnMouseOut>
                      <Icon source={InfoIcon} tone="subdued" />
                    </Tooltip>
                  </InlineStack>
                  {data?.revenue?.byCurrency && Object.keys(data.revenue.byCurrency).length > 1 ? (
                    <InlineStack gap="300" blockAlign="center">
                      {Object.entries(data.revenue.byCurrency).map(([cur, amount]) => (
                        <Text key={cur} as="p" variant="headingLg">
                          {formatCurrency(amount, cur)}
                        </Text>
                      ))}
                    </InlineStack>
                  ) : (
                    <Text as="p" variant="headingXl">
                      {formatCurrency(data?.revenue?.total ?? 0, data?.revenue?.currency ?? 'CAD')}
                    </Text>
                  )}
                  <Text as="p" variant="bodySm" tone="subdued">
                    since launch
                  </Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <InlineStack gap="100" blockAlign="center">
                    <Text as="h3" variant="headingSm" tone="subdued">
                      Uncaptured Revenue
                    </Text>
                    <Tooltip content="Sum of Shopify order totals for unpaid draft orders (status is OPEN or INVOICE_SENT, not yet COMPLETED)." dismissOnMouseOut>
                      <Icon source={InfoIcon} tone="subdued" />
                    </Tooltip>
                  </InlineStack>
                  {data?.unpaidRevenue?.byCurrency && Object.keys(data.unpaidRevenue.byCurrency).length > 1 ? (
                    <InlineStack gap="300" blockAlign="center">
                      {Object.entries(data.unpaidRevenue.byCurrency).map(([cur, amount]) => (
                        <Text key={cur} as="p" variant="headingLg">
                          {formatCurrency(amount, cur)}
                        </Text>
                      ))}
                    </InlineStack>
                  ) : (
                    <Text as="p" variant="headingXl">
                      {formatCurrency(data?.unpaidRevenue?.total ?? 0, data?.unpaidRevenue?.currency ?? 'CAD')}
                    </Text>
                  )}
                  <Text as="p" variant="bodySm" tone="subdued">
                    {data?.unpaidRevenue?.orderCount ?? 0} draft orders
                  </Text>
                </BlockStack>
              </Card>
            </InlineGrid>
          </Layout.Section>

          {/* Third row: Pipeline Stages */}
          <Layout.Section>
            <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingSm" tone="subdued">
                    Pipeline Stages
                  </Text>
                  <InlineStack gap="200" wrap>
                    {ALL_STAGES.map((stage, idx) => {
                      const count = data?.stageCounts?.[stage] ?? 0;
                      const nextStage = ALL_STAGES[idx + 1];
                      const nextCount = nextStage ? (data?.stageCounts?.[nextStage] ?? 0) : null;
                      const isBottleneck = count > 0 && nextCount !== null && (nextCount === 0 || count > nextCount * 1.5);
                      return (
                        <div
                          key={stage}
                          onClick={() => navigate(`/orders?stage=${stage}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Badge tone={count > 0 ? STAGE_TONES[stage] : undefined}>
                            {`${isBottleneck ? '⚠️ ' : ''}${STAGE_LABELS[stage]}: ${count}`}
                          </Badge>
                        </div>
                      );
                    })}
                  </InlineStack>
                </BlockStack>
              </Card>
          </Layout.Section>

          {/* Mockup Volume Chart */}
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <InlineStack gap="100" blockAlign="center">
                    <Text as="h3" variant="headingSm" tone="subdued">
                      Free Mockup Requests
                    </Text>
                    <Tooltip content="Daily count of free mockup requests submitted. Includes historical Jotform data (if imported) for comparison with the new app workflow." dismissOnMouseOut>
                      <Icon source={InfoIcon} tone="subdued" />
                    </Tooltip>
                  </InlineStack>
                  <InlineStack gap="200" blockAlign="center">
                    {mockupCountries.length > 0 && (
                      <div style={{ width: 140 }}>
                        <Select
                          label=""
                          labelHidden
                          options={[
                            { label: 'All Countries', value: '' },
                            ...mockupCountries.map((c) => ({ label: c, value: c })),
                          ]}
                          value={mockupCountry}
                          onChange={setMockupCountry}
                        />
                      </div>
                    )}
                    <div style={{ width: 140 }}>
                      <Select
                        label=""
                        labelHidden
                        options={[
                          { label: 'Last 7 days', value: '7d' },
                          { label: 'Last 30 days', value: '30d' },
                          { label: 'Last 90 days', value: '90d' },
                          { label: 'All time', value: 'all' },
                        ]}
                        value={mockupRange}
                        onChange={(v) => setMockupRange(v as TimeRange)}
                      />
                    </div>
                  </InlineStack>
                </InlineStack>
                {mockupLoading ? (
                  <SkeletonBodyText lines={6} />
                ) : filteredMockupData.length === 0 ? (
                  <Text as="p" tone="subdued">
                    No mockup data for this period
                  </Text>
                ) : (
                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={filteredMockupData}
                        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e1e3e5" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={formatDateLabel}
                          fontSize={12}
                          tick={{ fill: '#6d7175' }}
                          interval={filteredMockupData.length > 30 ? Math.floor(filteredMockupData.length / 15) : 0}
                        />
                        <YAxis
                          allowDecimals={false}
                          fontSize={12}
                          tick={{ fill: '#6d7175' }}
                        />
                        <RechartsTooltip
                          labelFormatter={(label) => formatDateLabel(String(label))}
                          contentStyle={{ fontSize: 13, borderRadius: 8 }}
                        />
                        {hasJotformData && (
                          <Legend />
                        )}
                        <Bar
                          dataKey="app"
                          name="App"
                          fill="#2c6ecb"
                          radius={[2, 2, 0, 0]}
                          stackId="mockups"
                        />
                        {hasJotformData && (
                          <Bar
                            dataKey="jotform"
                            name="Jotform (historical)"
                            fill="#95c9b4"
                            radius={[2, 2, 0, 0]}
                            stackId="mockups"
                          />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

        </Layout>
      )}
    </Page>
  );
}
